import { cn } from 'cn';
import { Check, CheckCircle2, FileText, MessageCircle, RefreshCw, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  VIRTUAL_REGISTRO_ID,
  TOTAL_ETAPAS_REGISTRO,
  contarEtapasConcluidas,
  isFalta,
  type AtividadeCasa,
  type Autonomia,
  type Boletim,
  type Chegada,
  type Comportamento,
  type Desempenho,
  type Foco,
  type MateriaOutputType,
  type ProfessorOutputType,
  type RegistroInputType,
  type RegistroResumoOutputType,
  type RegistroUpdateInputType,
} from '@shared/dto';
import { criarLinkWhatsapp } from '@shared/whatsapp';

import { FeatureGate } from '../../../components/common/feature-gate';
import { SegmentedProgress } from '../../../components/common/segmented-progress';
import {
  ATIVIDADE_CASA_LABEL,
  AUTONOMIA_LABEL,
  BOLETIM_LABEL,
  CHEGADA_LABEL,
  COMPORTAMENTO_LABEL,
  DESEMPENHO_LABEL,
  FOCO_LABEL,
} from '../../../components/common/registro-form/enum-labels';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '../../../components/ui/toggle-group';
import { useApiMutation, useApiQuery } from '../../../hooks/use-api';
import { materiasApi } from '../../../api/materias.api';
import { registrosApi } from '../../../api/registros.api';
import {
  ATIVIDADE_CASA_EMOJI,
  AUTONOMIA_EMOJI,
  BOLETIM_EMOJI,
  CHEGADA_EMOJI,
  COMPORTAMENTO_EMOJI,
  DESEMPENHO_EMOJI,
  FOCO_EMOJI,
} from '../../../lib/registro-emojis';
function Secao({
  titulo,
  acao,
  children,
}: {
  titulo: string;
  acao?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">{titulo}</p>
        {acao}
      </div>
      {children}
    </div>
  );
}

function OpcaoBotao({
  emoji,
  label,
  selecionado,
  disabled,
  onClick,
}: {
  emoji: string;
  label: string;
  selecionado: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-xl border px-3 py-3 text-left text-sm font-medium transition-all duration-150 disabled:pointer-events-none disabled:opacity-50',
        selecionado
          ? 'border-primary bg-primary/10 text-primary shadow-sm'
          : 'border-border bg-card text-foreground hover:bg-muted/60',
      )}
    >
      <span className="text-lg leading-none">{emoji}</span>
      <span className="flex-1">{label}</span>
      {selecionado ? <Check className="ml-auto size-4 shrink-0" /> : null}
    </button>
  );
}

function GradeOpcoes<T extends string>({
  valores,
  emojiMap,
  labelMap,
  atual,
  disabled,
  onSelecionar,
  colunas,
}: {
  valores: readonly T[];
  emojiMap: Record<T, string>;
  labelMap: Record<T, string>;
  atual: T | null;
  disabled?: boolean;
  onSelecionar: (valor: T) => void;
  colunas: 2 | 4;
}) {
  return (
    <div
      className={cn(
        'grid gap-2',
        colunas === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2 sm:grid-cols-4',
      )}
    >
      {valores.map((valor) => (
        <OpcaoBotao
          key={valor}
          emoji={emojiMap[valor]}
          label={labelMap[valor]}
          selecionado={atual === valor}
          disabled={disabled}
          onClick={() => onSelecionar(valor)}
        />
      ))}
    </div>
  );
}

/**
 * Só abre para linhas Não iniciado/Pendente (Concluído abre `HistoricoDialog`). Sem autosave por campo: um `PUT` por
 * clique sem tratar falha avançava o progresso local sem gravar quando a rede caía. Duas escritas: (1) Chegada cria
 * o registro (`POST`, só na 1ª vez); (2) o resto fica local até "Salvar registro" (não fecha o diálogo) ou "Gerar
 * relatório" (salva, persiste o texto-base e revela o editor). Editar o texto ou usar IA volta a deixar o form
 * pendente; WhatsApp e confirmação de envio só liberam com a versão salva.
 */
export function RegistrarAulaDialog({
  open,
  onOpenChange,
  resumo,
  bloqueadoFuturo,
  onSalvo,
  professores,
  materias,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resumo: RegistroResumoOutputType | null;
  /** Data futura: bloqueia só a escrita. */
  bloqueadoFuturo: boolean;
  onSalvo: () => void;
  professores: ProfessorOutputType[];
  materias: MateriaOutputType[];
}) {
  const idInicial = resumo && resumo.id !== VIRTUAL_REGISTRO_ID ? resumo.id : null;

  const [registroId, setRegistroId] = useState(idInicial);
  const [chegada, setChegada] = useState<Chegada | null>(resumo?.chegada ?? null);
  const [boletim, setBoletim] = useState<Boletim | null>(resumo?.boletim ?? null);
  const [atividadeCasa, setAtividadeCasa] = useState<AtividadeCasa | null>(
    resumo?.atividadeCasa ?? null,
  );
  const [foco, setFoco] = useState<Foco | null>(resumo?.foco ?? null);
  const [autonomia, setAutonomia] = useState<Autonomia | null>(resumo?.autonomia ?? null);
  const [comportamento, setComportamento] = useState<Comportamento | null>(
    resumo?.comportamento ?? null,
  );
  const [desempenho, setDesempenho] = useState<Desempenho | null>(resumo?.desempenho ?? null);
  const [conteudoIds, setConteudoIds] = useState<string[]>([]);
  const [anotacao, setAnotacao] = useState('');
  const [mostrarObservacao, setMostrarObservacao] = useState(false);
  const [relatorioTexto, setRelatorioTexto] = useState(resumo?.relatorioTexto ?? '');
  const [relatorioEnviadoEm, setRelatorioEnviadoEm] = useState<Date | string | null>(
    resumo?.relatorioEnviadoEm ?? null,
  );
  const [melhoradoEm, setMelhoradoEm] = useState<Date | string | null>(resumo?.melhoradoEm ?? null);
  const [alteracoesPendentes, setAlteracoesPendentes] = useState(false);

  const { data: detalhe } = useApiQuery(registrosApi.buscar, idInicial ?? '', {
    enabled: Boolean(idInicial),
  });
  const { data: conteudosDaMateria } = useApiQuery(
    materiasApi.listarConteudos,
    resumo?.materiaId ?? '',
    { enabled: Boolean(resumo) },
  );

  useEffect(() => {
    if (!detalhe) {
      return;
    }
    setConteudoIds(detalhe.conteudoIds);
    if (detalhe.anotacao) {
      setAnotacao(detalhe.anotacao);
      setMostrarObservacao(true);
    }
    setRelatorioTexto(detalhe.relatorioTexto ?? '');
    setRelatorioEnviadoEm(detalhe.relatorioEnviadoEm);
    setMelhoradoEm(detalhe.melhoradoEm);
    setAlteracoesPendentes(false);
  }, [detalhe]);

  const { mutate: criar, loading: criando } = useApiMutation(registrosApi.criar);
  const { mutate: atualizar, loading: enviando } = useApiMutation(registrosApi.atualizar);
  const { mutate: melhorar, loading: melhorando } = useApiMutation(registrosApi.melhorarRelatorio);
  const { mutate: marcarEnviado, loading: marcandoEnviado } = useApiMutation(
    registrosApi.marcarRelatorioEnviado,
  );

  const progressoRegistro = {
    chegada,
    boletim,
    atividadeCasa,
    foco,
    autonomia,
    comportamento,
    desempenho,
    relatorioTexto: relatorioTexto || null,
    relatorioEnviadoEm,
  };
  const etapasConcluidas = contarEtapasConcluidas(progressoRegistro);
  const falta = isFalta(progressoRegistro);
  const revelarNotas = chegada === 'PRESENTE' || chegada === 'ATRASADO';
  // Cobre também `salvar(true)`, que gera o relatório (ver `aoGerarRelatorio`).
  const salvandoOuGerando = enviando;
  const podeUsarRelatorio =
    Boolean(registroId && relatorioTexto.trim()) &&
    !alteracoesPendentes &&
    !salvandoOuGerando &&
    !melhorando &&
    !marcandoEnviado;
  const urlWhatsapp = criarLinkWhatsapp(resumo?.alunoWhatsapp ?? null, relatorioTexto);

  async function aoMudarChegada(novo: Chegada) {
    if (!resumo || bloqueadoFuturo) {
      return;
    }

    if (!registroId) {
      const corpo: RegistroInputType = {
        horarioId: resumo.horarioId,
        data: resumo.data,
        chegada: novo,
      } as unknown as RegistroInputType;
      const criado = await criar(corpo);
      setChegada(novo);
      setRegistroId(criado.id);
      setRelatorioTexto(criado.relatorioTexto ?? '');
      setRelatorioEnviadoEm(criado.relatorioEnviadoEm);
      setMelhoradoEm(criado.melhoradoEm);
      setAlteracoesPendentes(false);
      return;
    }

    // Registro reaberto (ja tinha chegada) -- so estado local; vai junto no "Enviar".
    setChegada(novo);
    setAlteracoesPendentes(true);
  }

  // `regenerarRelatorio` funde "salvar + gerar" num só PUT: o backend recompõe o texto-base, então `relatorioTexto` local nem é enviado.
  async function salvar(regenerarRelatorio = false) {
    if (!registroId) {
      return;
    }
    const corpo: RegistroUpdateInputType = {
      chegada: chegada ?? undefined,
      boletim: boletim ?? undefined,
      atividadeCasa: atividadeCasa ?? undefined,
      foco: foco ?? undefined,
      autonomia: autonomia ?? undefined,
      comportamento: comportamento ?? undefined,
      desempenho: desempenho ?? undefined,
      conteudoIds,
      anotacao,
      relatorioTexto: regenerarRelatorio ? undefined : relatorioTexto || null,
      regenerarRelatorio: regenerarRelatorio || undefined,
    };
    const salvo = await atualizar({ id: registroId, dados: corpo });
    setRelatorioTexto(salvo.relatorioTexto ?? '');
    setRelatorioEnviadoEm(salvo.relatorioEnviadoEm);
    setMelhoradoEm(salvo.melhoradoEm);
    setAlteracoesPendentes(false);
    onSalvo();
    return salvo;
  }

  async function aoGerarRelatorio() {
    if (!registroId) {
      return;
    }
    await salvar(true);
  }

  // O backend já persiste o texto melhorado: não deixa `alteracoesPendentes`; quem bloqueia novo uso é `melhoradoEm`.
  async function aoMelhorar() {
    if (!registroId) {
      return;
    }
    const resultado = await melhorar(registroId);
    setRelatorioTexto(resultado.relatorioTexto ?? '');
    setRelatorioEnviadoEm(resultado.relatorioEnviadoEm);
    setMelhoradoEm(resultado.melhoradoEm);
    setAlteracoesPendentes(false);
    onSalvo();
  }

  async function aoMarcarEnviado() {
    if (!registroId) {
      return;
    }
    const resultado = await marcarEnviado(registroId);
    setRelatorioEnviadoEm(resultado.relatorioEnviadoEm);
    onSalvo();
  }

  if (!resumo) {
    return null;
  }

  const materiaNome = materias.find((m) => m.id === resumo.materiaId)?.nome ?? '—';
  const professorNome = professores.find((p) => p.id === resumo.professorId)?.nome ?? '—';

  // Fechar pelo X/Esc salva pendências; "Salvar registro" nunca fecha (o professor segue pra gerar, revisar e enviar).
  async function aoMudarAbertura(novoOpen: boolean) {
    if (novoOpen) {
      onOpenChange(true);
      return;
    }
    if (!bloqueadoFuturo && registroId && alteracoesPendentes) {
      try {
        await salvar();
      } catch {
        // Falha já mostrou toast (useApiMutation) -- mantém o dialog aberto.
        return;
      }
    }
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={(novoOpen) => void aoMudarAbertura(novoOpen)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-500">{resumo.horarioPrevisto}</span>
            <span>{resumo.alunoNome}</span>
            <span className="text-xs font-normal text-muted-foreground">
              · {materiaNome} · {professorNome}
            </span>
          </DialogTitle>
        </DialogHeader>

        {registroId && !falta ? (
          <SegmentedProgress total={TOTAL_ETAPAS_REGISTRO} preenchido={etapasConcluidas} />
        ) : null}

        <div className="space-y-4">
          <Secao titulo="Chegada">
            <GradeOpcoes
              valores={['PRESENTE', 'ATRASADO', 'FALTOU'] as const}
              emojiMap={CHEGADA_EMOJI}
              labelMap={CHEGADA_LABEL}
              atual={chegada}
              disabled={criando || salvandoOuGerando || bloqueadoFuturo}
              onSelecionar={(v) => void aoMudarChegada(v)}
              colunas={2}
            />
            {bloqueadoFuturo ? (
              <p className="mt-2 text-xs text-muted-foreground">Esta aula ainda não aconteceu.</p>
            ) : null}
          </Secao>

          {revelarNotas ? (
            <>
              <Secao titulo="Boletim">
                <GradeOpcoes
                  valores={['PEGOU', 'NAO_PEGOU', 'PROBLEMA'] as const}
                  emojiMap={BOLETIM_EMOJI}
                  labelMap={BOLETIM_LABEL}
                  atual={boletim}
                  disabled={salvandoOuGerando || bloqueadoFuturo}
                  onSelecionar={(valor) => {
                    setBoletim(valor);
                    setAlteracoesPendentes(true);
                  }}
                  colunas={2}
                />
              </Secao>

              <Secao titulo="Atividade de casa">
                <GradeOpcoes
                  valores={['FEZ', 'FEZ_PARCIALMENTE', 'NAO_FEZ', 'NAO_HAVIA'] as const}
                  emojiMap={ATIVIDADE_CASA_EMOJI}
                  labelMap={ATIVIDADE_CASA_LABEL}
                  atual={atividadeCasa}
                  disabled={salvandoOuGerando || bloqueadoFuturo}
                  onSelecionar={(valor) => {
                    setAtividadeCasa(valor);
                    setAlteracoesPendentes(true);
                  }}
                  colunas={4}
                />
              </Secao>

              <Secao titulo="Postura em aula">
                <div className="space-y-3">
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Foco</p>
                    <GradeOpcoes
                      valores={Object.keys(FOCO_LABEL) as Foco[]}
                      emojiMap={FOCO_EMOJI}
                      labelMap={FOCO_LABEL}
                      atual={foco}
                      disabled={salvandoOuGerando || bloqueadoFuturo}
                      onSelecionar={(valor) => {
                        setFoco(valor);
                        setAlteracoesPendentes(true);
                      }}
                      colunas={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Autonomia</p>
                    <GradeOpcoes
                      valores={Object.keys(AUTONOMIA_LABEL) as Autonomia[]}
                      emojiMap={AUTONOMIA_EMOJI}
                      labelMap={AUTONOMIA_LABEL}
                      atual={autonomia}
                      disabled={salvandoOuGerando || bloqueadoFuturo}
                      onSelecionar={(valor) => {
                        setAutonomia(valor);
                        setAlteracoesPendentes(true);
                      }}
                      colunas={4}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">Comportamento</p>
                    <GradeOpcoes
                      valores={Object.keys(COMPORTAMENTO_LABEL) as Comportamento[]}
                      emojiMap={COMPORTAMENTO_EMOJI}
                      labelMap={COMPORTAMENTO_LABEL}
                      atual={comportamento}
                      disabled={salvandoOuGerando || bloqueadoFuturo}
                      onSelecionar={(valor) => {
                        setComportamento(valor);
                        setAlteracoesPendentes(true);
                      }}
                      colunas={4}
                    />
                  </div>
                </div>
              </Secao>

              <Secao titulo="Desempenho na aula">
                <GradeOpcoes
                  valores={Object.keys(DESEMPENHO_LABEL) as Desempenho[]}
                  emojiMap={DESEMPENHO_EMOJI}
                  labelMap={DESEMPENHO_LABEL}
                  atual={desempenho}
                  disabled={salvandoOuGerando || bloqueadoFuturo}
                  onSelecionar={(valor) => {
                    setDesempenho(valor);
                    setAlteracoesPendentes(true);
                  }}
                  colunas={4}
                />
              </Secao>

              <Secao titulo="Conteúdos trabalhados">
                <ToggleGroup
                  type="multiple"
                  variant="outline"
                  value={conteudoIds}
                  onValueChange={(valores) => {
                    setConteudoIds(valores);
                    setAlteracoesPendentes(true);
                  }}
                  disabled={salvandoOuGerando || bloqueadoFuturo}
                  className="flex-wrap justify-start"
                >
                  {conteudosDaMateria?.map((conteudo) => (
                    <ToggleGroupItem key={conteudo.id} value={conteudo.id}>
                      {conteudo.nome}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </Secao>

              <div className="rounded-2xl border border-border bg-card p-4">
                {mostrarObservacao || anotacao ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Observação</p>
                    <Textarea
                      value={anotacao}
                      disabled={salvandoOuGerando || bloqueadoFuturo}
                      onChange={(evento) => {
                        setAnotacao(evento.target.value);
                        setAlteracoesPendentes(true);
                      }}
                    />
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-sm"
                    onClick={() => setMostrarObservacao(true)}
                  >
                    + Adicionar observação
                  </Button>
                )}
              </div>
            </>
          ) : null}

          {registroId && !falta && !relatorioTexto.trim() ? (
            <div className="rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-5">
              <div className="flex items-start gap-3">
                <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
                  <FileText className="size-5" />
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  <div>
                    <p className="font-semibold">Relatório para a família</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Salvaremos os dados preenchidos e criaremos uma mensagem pronta para revisão.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="lg"
                    className="h-11 w-full"
                    onClick={() => void aoGerarRelatorio()}
                    disabled={salvandoOuGerando || bloqueadoFuturo}
                  >
                    {salvandoOuGerando ? 'Gerando e salvando...' : 'Gerar relatório'}
                  </Button>
                </div>
              </div>
            </div>
          ) : registroId && !falta ? (
            <Secao
              titulo="Mensagem para a família"
              acao={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => void aoGerarRelatorio()}
                  disabled={salvandoOuGerando || melhorando || bloqueadoFuturo}
                  title="Atualizar mensagem com as notas atuais"
                >
                  <RefreshCw className="size-4" />
                </Button>
              }
            >
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Revise e edite a mensagem. Mudou uma nota acima? Use o botão de atualizar pra
                  reaplicar no texto.
                </p>
                <Textarea
                  aria-label="Mensagem do relatório"
                  rows={7}
                  maxLength={4000}
                  value={relatorioTexto}
                  disabled={salvandoOuGerando || bloqueadoFuturo}
                  onChange={(evento) => {
                    setRelatorioTexto(evento.target.value);
                    setAlteracoesPendentes(true);
                  }}
                />
                {alteracoesPendentes ? (
                  <p className="text-xs font-medium text-amber-700">
                    Salve as alterações para liberar WhatsApp e confirmação de envio.
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {!melhoradoEm ? (
                    <FeatureGate feature="RELATORIO_IA">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void aoMelhorar()}
                        disabled={!podeUsarRelatorio || bloqueadoFuturo}
                      >
                        <Sparkles className="size-4" />
                        {melhorando ? 'Melhorando...' : 'Melhorar com IA'}
                      </Button>
                    </FeatureGate>
                  ) : null}
                  {podeUsarRelatorio && urlWhatsapp ? (
                    <Button asChild className="bg-emerald-600 hover:bg-emerald-700">
                      <a href={urlWhatsapp} target="_blank" rel="noreferrer">
                        <MessageCircle className="size-4" />
                        Abrir WhatsApp
                      </a>
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled
                      title={
                        !resumo.alunoWhatsapp
                          ? 'Cadastre o WhatsApp do aluno para enviar.'
                          : undefined
                      }
                    >
                      <MessageCircle className="size-4" />
                      Abrir WhatsApp
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={relatorioEnviadoEm ? 'secondary' : 'ghost'}
                    onClick={() => void aoMarcarEnviado()}
                    disabled={!podeUsarRelatorio || Boolean(relatorioEnviadoEm) || bloqueadoFuturo}
                  >
                    <CheckCircle2 className="size-4" />
                    {relatorioEnviadoEm ? 'Enviado' : 'Marcar como enviado'}
                  </Button>
                </div>
                {!resumo.alunoWhatsapp ? (
                  <p className="text-xs text-amber-700">
                    Este aluno ainda não tem WhatsApp cadastrado.
                  </p>
                ) : null}
              </div>
            </Secao>
          ) : null}
        </div>

        <div className="flex items-center justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {registroId && !alteracoesPendentes
              ? 'Alterações salvas'
              : 'Existem alterações pendentes'}
          </p>
          <Button
            type="button"
            onClick={() => void salvar()}
            disabled={!registroId || !alteracoesPendentes || salvandoOuGerando || bloqueadoFuturo}
          >
            {enviando ? 'Salvando...' : 'Salvar registro'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
